import React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const Error404Page = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const mailto = `mailto:support@scraptrade.com.au?subject=Page%20Not%20Found%20(404)&body=Hi%20team,%0A%0AI%20hit%20a%20404%20on:%20${encodeURIComponent(
    location.pathname
  )}%0ATime:%20${encodeURIComponent(new Date().toLocaleString())}%0A%0AThanks.`;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        // Gray tone background with subtle grid
        backgroundImage: `
          radial-gradient(circle at 2px 2px, rgba(0,0,0,0.05) 2px, transparent 0)`,
        backgroundSize: "24px 24px",
        backgroundColor: "#f2f3f5",
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1200px 600px at 10% 10%, rgba(0,0,0,0.04), transparent), radial-gradient(1000px 500px at 90% 120%, rgba(0,0,0,0.05), transparent), linear-gradient(135deg, #f7f8f9 0%, #e8eaee 100%)",
          zIndex: 0,
        },
      }}
    >
      {/* Decorative blur blobs */}
      <Box
        sx={{
          position: "absolute",
          width: 260,
          height: 260,
          borderRadius: "50%",
          right: { xs: -80, md: -40 },
          top: { xs: -60, md: 40 },
          filter: "blur(70px)",
          background: "rgba(120,120,120,0.18)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          left: { xs: -60, md: 40 },
          bottom: { xs: -60, md: 40 },
          filter: "blur(80px)",
          background: "rgba(80,80,80,0.15)",
          zIndex: 0,
        }}
      />

      {/* Card with gradient border + glass */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: 1.2,
          borderRadius: 5,
          background:
            "linear-gradient(135deg, rgba(160,160,160,0.35), rgba(210,210,210,0.35))",
          boxShadow:
            "0 10px 30px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.5)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 760,
            width: "clamp(320px, 90vw, 760px)",
            borderRadius: 4,
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 6 },
            textAlign: "center",
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            {/* Floating icon */}
            <Box
              component={motion.div}
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              sx={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background:
                  "radial-gradient(circle at 30% 30%, #f4c542, #d6d6d6)", // gold accent
                boxShadow:
                  "inset 0 1px 1px rgba(255,255,255,0.7), 0 10px 24px rgba(0,0,0,0.10)",
              }}
            >
              <SearchOffOutlinedIcon sx={{ fontSize: 44, color: "grey.700" }} />
            </Box>

            {/* Big code + title */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "3rem", sm: "4rem" },
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                background:
                  "linear-gradient(90deg, #F4C542 0%, #737373 50%, #9b9b9b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0.5,
              }}
            >
              404
            </Typography>

            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: "grey.800", mb: 0.5 }}
            >
              Page Not Found
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "text.secondary", maxWidth: 540 }}
            >
              The page you are looking for doesn’t exist or may have been moved.
              Try going back or return to a safe page.
            </Typography>

            {/* Context chips */}
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Chip size="small" label="Resource missing" />
              <Chip size="small" variant="outlined" label={location.pathname} />
            </Stack>

            {/* Actions */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mt: 3, width: "100%", justifyContent: "center" }}
            >
              <Button
                startIcon={<HomeOutlinedIcon />}
                variant="contained"
                onClick={() => navigate("/")}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  bgcolor: "#F4C542",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#e3b936" },
                  boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
                }}
              >
                Home
              </Button>

              <Button
                startIcon={<StorefrontOutlinedIcon />}
                variant="outlined"
                onClick={() => navigate("/market")}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  borderColor: "grey.400",
                  color: "grey.800",
                  "&:hover": {
                    borderColor: "#e3b936",
                    backgroundColor: "grey.100",
                  },
                }}
              >
                Browse Marketplace
              </Button>

              <Button
                startIcon={<SupportAgentIcon />}
                variant="text"
                onClick={() => (window.location.href = mailto)}
                sx={{
                  borderRadius: 3,
                  px: 2,
                  py: 1.2,
                  textTransform: "none",
                  color: "grey.700",
                }}
              >
                Contact Support
              </Button>
            </Stack>

            {/* Technical details */}
            <Divider sx={{ width: "100%", mt: 3 }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                Technical details
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpen((v) => !v)}
                sx={{ color: "grey.700" }}
                aria-label="toggle technical details"
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Stack>

            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 1,
                  px: 2,
                  py: 1.5,
                  textAlign: "left",
                  bgcolor: "grey.100",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="caption" sx={{ display: "block" }}>
                  Path: {location.pathname}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Time: {new Date().toLocaleString()}
                </Typography>
              </Box>
            </Collapse>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default Error404Page;
